/**
 * 从 UserAgent 中获取指定字段的值
 * 
 * 解析 UserAgent 字符串，提取指定字段名后面的值。
 * 常用于获取应用版本号、设备信息等自定义字段。
 * 
 * @param name - 要查找的字段名
 * @returns 字段对应的值，如果未找到则返回 false
 * @throws {TypeError} 当参数不是字符串时抛出
 * 
 * @example
 * ```ts
 * import { getFieldFromUa } from '@fu/matrix';
 * 
 * // UserAgent: "Mozilla/5.0 (iPhone; appVersion/2.1.0; theme/dark) Safari/537.36"
 * getFieldFromUa('appVersion'); // => "2.1.0"
 * getFieldFromUa('theme'); // => "dark"
 * getFieldFromUa('notExist'); // => false
 * 
 * // 同花顺 UserAgent 示例
 * // "iHexin/10.80.60 (iPhone; iOS 16.0; zh_CN)"
 * getFieldFromUa('iHexin'); // => "10.80.60"
 * ```
 * 
 * @since 1.0.0
 */
export function getFieldFromUa(name: string): string | false {
  // 参数验证
  if (typeof name !== 'string') {
    throw new TypeError('字段名必须是字符串');
  }

  if (name.trim() === '') {
    throw new TypeError('字段名不能为空');
  }

  // 环境检查
  if (typeof navigator === 'undefined' || typeof navigator.userAgent !== 'string') {
    return false;
  }

  const ua = navigator.userAgent;
  const index = ua.indexOf(name);
  
  if (index < 0) {
    return false;
  }

  // 获取字段名后面的内容，跳过字段名和分隔符（通常是 '/' 或 ' '）
  const startIndex = index + name.length;
  
  // 检查是否有分隔符
  if (startIndex >= ua.length) {
    return false;
  }

  // 常见的分隔符：'/', ' ', '='
  const separator = ua.charAt(startIndex);
  if (separator !== '/' && separator !== ' ' && separator !== '=') {
    return false;
  }

  // 提取值，直到下一个空格或字符串结束
  const valueStart = startIndex + 1;
  if (valueStart >= ua.length) {
    return false;
  }

  const remainingUa = ua.substring(valueStart);
  const value = remainingUa.split(' ')[0].split(';')[0].split(')')[0];
  
  return value || false;
} 